/**
 * External dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  RadioGroup,
  RadioGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import Tiptap from '@Components/TipTap';
import AiSnippetPickerModal from '../AiSnippetPickerModal';
import { UpdateTicketedEventPayload } from '@Types/api';
import { TicketedEvent } from '@Types/ticketedEvents';
import { useUpdateTicketedEvent } from '@Hooks/useUpdateTicketedEvent';

const FormSchema = z
  .object({
    name: z.string().min(2, {
      message: 'A name is required.',
    }),
    description: z.string().min(1, {
      message: 'A description is required.',
    }),
    teaser: z.string().optional().nullable(),
    startDateTime: z.string().optional().nullable(),
    endDateTime: z.string().optional().nullable(),
    capacity: z
      .number()
      .min(1, {
        message: 'Please enter the number of guests',
      })
      .max(10000, {
        message: 'Max capacity is 10000',
      }),
    visibility: z.enum(['PUBLIC', 'MEMBERS_ONLY', 'PRIVATE']),
    status: z.enum(['DRAFT', 'PUBLISHED']),
  })
  .refine(
    (data) => {
      const start = data.startDateTime?.trim()
        ? new Date(data.startDateTime).getTime()
        : null;
      const end = data.endDateTime?.trim()
        ? new Date(data.endDateTime).getTime()
        : null;
      if (start !== null && end !== null && end < start) return false;
      return true;
    },
    {
      message: 'End date and time must be after start date and time.',
      path: ['endDateTime'],
    },
  );

type FormValues = z.infer<typeof FormSchema>;

export interface EditTicketedEventDetailsSheetProps
  extends React.PropsWithChildren {
  event: TicketedEvent;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

// Convert ISO datetime to datetime-local format
const toDateTimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Convert datetime-local format to ISO string
const toISO = (dateTimeLocal: string | null | undefined): string | null => {
  if (!dateTimeLocal || !dateTimeLocal.trim()) return null;
  return new Date(dateTimeLocal).toISOString();
};

export default function EditTicketedEventDetailsSheet({
  event,
  isOpen,
  setIsOpen,
}: EditTicketedEventDetailsSheetProps) {
  const [snippetModalOpen, setSnippetModalOpen] = useState(false);

  const updateMutation = useUpdateTicketedEvent({
    onSuccess: () => {
      toastSuccess('Event details updated successfully.');
      setIsOpen(false);
    },
    onError: (error) => {
      console.error('Error updating event:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to update this event. Please check your changes and try again.',
      });
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: event.name,
      description: event.description ?? '',
      teaser: event.teaser ?? '',
      startDateTime: toDateTimeLocal(event.startDateTime),
      endDateTime: toDateTimeLocal(event.endDateTime),
      capacity: event.capacity,
      visibility: event.visibility,
      status: event.status,
    },
  });

  // Keep form in sync if event changes while sheet is open
  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: event.name,
        description: event.description ?? '',
        teaser: event.teaser ?? '',
        startDateTime: toDateTimeLocal(event.startDateTime),
        endDateTime: toDateTimeLocal(event.endDateTime),
        capacity: event.capacity,
        visibility: event.visibility,
        status: event.status,
      });
      form.clearErrors('root');
    }
  }, [event, isOpen, form]);

  const onSubmit = async (values: FormValues) => {
    const {
      name,
      description,
      teaser,
      startDateTime,
      endDateTime,
      capacity,
      visibility,
      status,
    } = values;

    // Build event object with all form values
    // The form validation ensures name and description are present
    const eventData: UpdateTicketedEventPayload['event'] = {
      name,
      description,
      capacity,
      visibility,
      status,
    };

    // Include optional fields only if they have values
    if (teaser && teaser.trim()) {
      eventData.teaser = teaser.trim();
    }

    const startDate = toISO(startDateTime);
    if (startDate) {
      eventData.startDateTime = startDate;
    }

    const endDate = toISO(endDateTime);
    if (endDate) {
      eventData.endDateTime = endDate;
    }

    const body: UpdateTicketedEventPayload = {
      event: eventData,
    };

    // Clear any previous root error
    form.clearErrors('root');

    await updateMutation.mutateAsync({
      eventId: event.id,
      payload: body,
    });
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      // Reset back to the current event values when closing
      form.reset({
        name: event.name,
        description: event.description ?? '',
        teaser: event.teaser ?? '',
        startDateTime: toDateTimeLocal(event.startDateTime),
        endDateTime: toDateTimeLocal(event.endDateTime),
        capacity: event.capacity,
        visibility: event.visibility,
        status: event.status,
      });
      form.clearErrors('root');
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>{`Edit ${event.name}'s details`}</SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {/* Top-level error banner */}
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  {/* Name */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Event name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Sushi Night: Omakase Experience"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Description</FormLabel>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSnippetModalOpen(true)}
                          >
                            Insert AI Snippet
                          </Button>
                        </div>
                        <FormControl>
                          <Tiptap
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="Describe your event..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <AiSnippetPickerModal
                    open={snippetModalOpen}
                    onOpenChange={setSnippetModalOpen}
                    onInsert={(tag) => {
                      const current = form.getValues('description') ?? '';
                      const sep =
                        current && !current.endsWith(' ') && !current.endsWith('>')
                          ? ' '
                          : '';
                      form.setValue('description', `${current}${sep}${tag}`, {
                        shouldDirty: true,
                      });
                    }}
                  />

                  {/* Teaser */}
                  <FormField
                    control={form.control}
                    name="teaser"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teaser</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Chef-guided tasting with seasonal specials."
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Start Date & Time */}
                  <FormField
                    control={form.control}
                    name="startDateTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date & Time</FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* End Date & Time */}
                  <FormField
                    control={form.control}
                    name="endDateTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End Date & Time</FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Capacity */}
                  <FormField
                    control={form.control}
                    name="capacity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Capacity</FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            placeholder="0"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value.replace(/[^\d]/g, '')
                                  ? Number(e.target.value.replace(/[^\d]/g, ''))
                                  : 0,
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Visibility */}
                  <FormField
                    control={form.control}
                    name="visibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Visibility</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col gap-2"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="PUBLIC"
                                id="visibility-public"
                              />
                              <label
                                htmlFor="visibility-public"
                                className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Public
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="MEMBERS_ONLY"
                                id="visibility-members"
                              />
                              <label
                                htmlFor="visibility-members"
                                className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Members only
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="PRIVATE"
                                id="visibility-private"
                              />
                              <label
                                htmlFor="visibility-private"
                                className="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Private
                              </label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Status */}
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                            <SelectItem value="PUBLISHED">Published</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={updateMutation.isPending}
                  disabled={updateMutation.isPending}
                >
                  Update
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
